import React, { Suspense, useEffect, useState } from 'react';
import { useWindowSize } from '../../hooks/useWindowSize';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import SidebarMenu from './SidebarMenu';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Drawer from '@mui/material/Drawer';
import Ads from './Ads';
import {
	Box,
	CircularProgress,
	CSSObject,
	Container,
	SwipeableDrawer,
	Theme,
	alpha,
	useTheme,
	Portal
} from '@mui/material';
import TopToolbar from './TopToolbar';
import NavBreadcrumbs from './NavBreadcrumbs';
import Logo from './Logo';
import TutorialWizard from '../ui/organisms/TutorialWizard';
import { useTutorial } from '../ui/organisms/TutorialContext';
import { useLocation } from 'react-router';
import {
	stepsDesktopAndMobilePage2,
	stepsDesktopAndMobilePage3,
	stepsDesktopAndMobilePage4,
	stepsDesktopPage1,
	stepsMobilePage1
} from '../ui/organisms/TutorialSteps';
import { Step } from 'react-joyride';
import AiAssistant from '../ui/organisms/AiAssistant';
import MultiAgents from '../ui/molecules/MultiAgents';
import DocumentRequestsAssistant from '../ui/organisms/DocumentRequestsAssistant';
import DailyUpdatesAssistant from '../ui/organisms/DailyUpdatesAssistant';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import DraggableChatbot from '../ui/organisms/DraggableChatbot';
import SkipLink from '../ui/atoms/SkipLink';
import { fetchPlatformSettings } from '../../redux/slices/platformSettings';
import { fetchFeatureFlagEnabledAt, fetchFeatureFlags } from '../../redux/slices/featureFlags';

const drawerWidth = 300;
const toolbarHeight = 5;
export const adsHeight = 5;

// Tour steps that target a feature-flagged menu item. When the feature is off, the menu
// item is not rendered, so the step must be dropped — otherwise Joyride anchors to a
// missing element and the index-based navigation in TutorialWizard skips the wrong steps.
const TOUR_STEP_FEATURE: Record<string, string> = {
	'.verification-menu-item': 'verifications-enabled',
	'.journey-profile-menu-item': 'my-journey-enabled',
	'.portal-menu-item': 'my-journey-enabled',
	'.my-enquiries-menu-item': 'my-journey-enabled',
	'.shared-wallet-menu-item': 'shared-wallet-enabled',
	'.faq-menu-item': 'faq-enabled'
};

// Flags that are enabled unless explicitly turned off; everything else is off by default.
const DEFAULT_ON_FLAGS = new Set(['advisor-support-enabled', 'app-tour-enabled']);

const isFeatureFlagEnabled = (flags: Record<string, boolean> | undefined, flag: string) =>
	DEFAULT_ON_FLAGS.has(flag) ? flags?.[flag] !== false : flags?.[flag] === true;

const filterTourSteps = (steps: Step[], flags: Record<string, boolean> | undefined) =>
	steps.filter((step) => {
		const target = typeof step.target === 'string' ? step.target : '';
		const feature = TOUR_STEP_FEATURE[target];
		return !feature || isFeatureFlagEnabled(flags, feature);
	});

const liquidGlassSurface = (theme: Theme): CSSObject => ({
	backgroundColor:
		theme.palette.mode === 'dark'
			? alpha(theme.palette.background.paper, 0.85)
			: alpha(theme.palette.background.paper, 0.75),
	backdropFilter: 'blur(var(--glass-blur, 20px))',
	WebkitBackdropFilter: 'blur(var(--glass-blur, 20px))',

	borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,

	boxShadow: `
		0 8px 32px ${alpha(theme.palette.common.black, 0.25)},
		inset 0 1px 0 ${alpha(theme.palette.common.white, 0.15)}
	`,

	backgroundImage: `
		linear-gradient(
			180deg,
			${alpha(theme.palette.common.white, 0.08)},
			${alpha(theme.palette.common.white, 0.02)}
		)
	`
});

const MobileLayout = ({
	children,
	header,
	actions,
	tourActive,
	steps
}: {
	children?: React.ReactElement | React.ReactElement[];
	header?: React.ReactElement;
	actions?: React.ReactElement;
	tourActive: boolean;
	steps: Step[];
}) => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
	const [aIAgentsModalOpen, setAIAgentsModalOpen] = useState(false);
	const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
	const theme = useTheme();
	const location = useLocation();
	const isAnaPage = location.pathname === '/ana';

	// Auto-collapse the mobile sidebar whenever navigation occurs (any menu selection
	// changes the URL), so the full-width drawer doesn't stay open over the content.
	useEffect(() => {
		setDrawerOpen(false);
	}, [location.pathname, location.search, location.hash]);

	const handleOpenAiAssistant = () => {
		setAiAssistantOpen(true);
	};

	const handleCloseAiAssistant = () => {
		setAiAssistantOpen(false);
	};

	const aiChatbotEnabled = useAppSelector((state) => {
		const orgCurrent = state.organisation.current;
		if (orgCurrent) return orgCurrent.aiChatbot ?? true;
		// Fallback: check the user's organisations (e.g. child users who never load org routes)
		const userOrgs = state.user.currentUser?.organisations;
		if (userOrgs?.length) return userOrgs.every((org) => org.aiChatbot !== false);
		return true;
	});

	const showAdvertisement = useAppSelector((state) => state.platformSettings.showAdvertisement);

	return (
		<>
			{tourActive && (
				<TutorialWizard isMobile={true} setDrawerOpen={setDrawerOpen} steps={steps}></TutorialWizard>
			)}
			<AppBar
				color="transparent"
				elevation={0}
				sx={{
					backdropFilter: 'blur(10px)',
					backgroundColor: alpha(theme.palette.background.paper, 0.3),
					pb: 2,
					borderBottom: `1px solid ${theme.palette.divider}`
				}}
			>
				<Toolbar sx={{ height: '6rem' }}>
					<Box display="flex" flexDirection="column" width="100%">
						<Box display="flex" justifyContent="space-between">
							<IconButton
								size="large"
								edge="start"
								color="inherit"
								aria-label="menu"
								className="menu-drawer-btn"
								onClick={() => {
									setDrawerOpen(true);
								}}
							>
								<MenuTwoToneIcon />
							</IconButton>
							<Logo />
							<TopToolbar showBreadCrumb={false} />
						</Box>
						<Box display="flex" justifyContent="space-between">
							<NavBreadcrumbs />
						</Box>
					</Box>
				</Toolbar>
			</AppBar>
			<SwipeableDrawer
				anchor="left"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				onOpen={() => setDrawerOpen(true)}
				sx={{
					'& .MuiDrawer-paper': {
						width: '100vw',
						maxWidth: '100%',
						...liquidGlassSurface(theme)
					}
				}}
			>
				<SidebarMenu></SidebarMenu>
			</SwipeableDrawer>
			<Box
				id="main-content"
				component="main"
				tabIndex={-1}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					paddingTop: '8rem',
					paddingBottom: showAdvertisement ? adsHeight + 'rem' : 0,
					minHeight: '100vh',
					gap: 2,
					overflow: 'hidden',
					'&:focus': { outline: 'none' },
					// The skip link sends keyboard focus to this landmark; show a visible
					// indicator for keyboard users (WCAG 2.4.7) without flashing an outline
					// when the container is clicked with a mouse.
					'&:focus-visible': {
						outline: `3px solid ${theme.palette.primary.main}`,
						outlineOffset: '-3px'
					},
					...(isAnaPage && { height: '100vh' })
				}}
			>
				{header}
				<Container
					maxWidth={false}
					disableGutters
					sx={{ display: 'flex', py: 2, px: 1.5, flex: 1, ...(isAnaPage && { minHeight: 0 }) }}
				>
					<Suspense
						fallback={
							<Box
								sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}
							>
								<CircularProgress size={32} />
							</Box>
						}
					>
						{children}
					</Suspense>
				</Container>
				{actions}
			</Box>
			{aiChatbotEnabled && !isAnaPage && (
				<Portal>
					<DraggableChatbot
						handleOpenAiAssistant={handleOpenAiAssistant}
						setAIAgentsModalOpen={setAIAgentsModalOpen}
						hidden={drawerOpen}
					/>
				</Portal>
			)}
			{showAdvertisement && (
				<Box
					sx={{
						position: 'fixed',
						bottom: 0,
						width: '100%',
						height: adsHeight + 'rem',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 1000
					}}
				>
					<Ads />
				</Box>
			)}

			<AiAssistant open={aiAssistantOpen} onClose={handleCloseAiAssistant} />
			<MultiAgents
				open={aIAgentsModalOpen}
				onClose={() => setAIAgentsModalOpen(false)}
				agentClick={setSelectedAgent}
			/>
			<DocumentRequestsAssistant
				open={selectedAgent === 'document-requests'}
				onClose={() => setSelectedAgent(null)}
			/>
			<DailyUpdatesAssistant open={selectedAgent === 'daily-updates'} onClose={() => setSelectedAgent(null)} />
		</>
	);
};

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	}),
	overflow: 'visible'
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	}),
	overflow: 'visible',
	width: `calc(${theme.spacing(8)} + 1px)`
});

const openedMixinContainer = (theme: Theme): CSSObject => ({
	marginLeft: drawerWidth + 'px',
	transition: theme.transitions.create('marginLeft', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	})
});

const closedMixinContainer = (theme: Theme): CSSObject => ({
	marginLeft: `calc(${theme.spacing(8)} + 1px)`,
	transition: theme.transitions.create('marginLeft', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	})
});
const openedMixinAds = (theme: Theme): CSSObject => ({
	marginLeft: drawerWidth + 'px',
	width: `calc( 100% - ${drawerWidth}px )`,
	transition: theme.transitions.create('marginLeft', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	})
});

const closedMixinAds = (theme: Theme): CSSObject => ({
	marginLeft: `calc(${theme.spacing(8)} + 1px)`,
	width: `calc( 100% - ${theme.spacing(8)} )`,
	transition: theme.transitions.create('marginLeft', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	})
});

const DesktopLayout = ({
	children,
	header,
	actions,
	tourActive,
	steps
}: {
	children?: React.ReactElement | React.ReactElement[];
	header?: React.ReactElement;
	actions?: React.ReactElement;
	tourActive: any;
	steps: Step[];
}) => {
	const theme = useTheme();
	const location = useLocation();
	const isAnaPage = location.pathname === '/ana';
	// Screens that benefit from a full-width content pane (e.g. data tables) rather
	// than the default centred, max-width-constrained reading column.
	const isWideContentPage =
		// Not a /admin/ path: the organisation roster is a wide data table on the same
		// AdminPageShell, and the default reading column cropped it into a narrow card.
		// Matched by pattern because the organisation id is a path segment.
		/^\/organisations\/[^/]+\/constituents/.test(location.pathname) ||
		location.pathname.startsWith('/admin/ai-assistant') ||
		location.pathname.startsWith('/admin/partners') ||
		location.pathname.startsWith('/admin/school-directory') ||
		location.pathname.startsWith('/admin/support') ||
		location.pathname.startsWith('/admin/appearance') ||
		location.pathname.startsWith('/admin/feature-flags') ||
		location.pathname.startsWith('/admin/legal') ||
		location.pathname.startsWith('/admin/faq') ||
		location.pathname.startsWith('/admin/system');
	const [drawerOpen, setDrawerOpen] = useState(true);
	const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
	const [aIAgentsModalOpen, setAIAgentsModalOpen] = useState(false);
	const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

	const handleOpenAiAssistant = () => {
		setAiAssistantOpen(true);
	};

	const handleCloseAiAssistant = () => {
		setAiAssistantOpen(false);
	};

	const aiChatbotEnabled = useAppSelector((state) => {
		const orgCurrent = state.organisation.current;
		if (orgCurrent) return orgCurrent.aiChatbot ?? true;
		// Fallback: check the user's organisations (e.g. child users who never load org routes)
		const userOrgs = state.user.currentUser?.organisations;
		if (userOrgs?.length) return userOrgs.every((org) => org.aiChatbot !== false);
		return true;
	});

	const showAdvertisement = useAppSelector((state) => state.platformSettings.showAdvertisement);

	return (
		<>
			{tourActive && <TutorialWizard isMobile={false} steps={steps}></TutorialWizard>}
			<AppBar
				color="transparent"
				elevation={0}
				sx={{
					backdropFilter: 'blur(10px)',
					backgroundColor: alpha(theme.palette.background.paper, 0.3)
				}}
			>
				<Toolbar
					sx={{
						...(drawerOpen && {
							...openedMixinContainer(theme)
						}),
						...(!drawerOpen && {
							...closedMixinContainer(theme)
						})
					}}
				>
					<TopToolbar height={toolbarHeight + 'rem'} />
				</Toolbar>
			</AppBar>
			<Drawer
				variant="permanent"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					whiteSpace: 'nowrap',
					boxSizing: 'border-box',
					...(drawerOpen && {
						...openedMixin(theme),
						'& .MuiDrawer-paper': {
							...openedMixin(theme),
							...liquidGlassSurface(theme)
						}
					}),
					...(!drawerOpen && {
						...closedMixin(theme),
						'& .MuiDrawer-paper': {
							...closedMixin(theme),
							...liquidGlassSurface(theme)
						}
					})
				}}
			>
				<Box
					id="iconButtonContainer"
					sx={{
						position: 'absolute',
						right: '-.75rem',
						display: 'flex',
						alignItems: 'center',
						height: toolbarHeight + 'rem'
					}}
				>
					<IconButton
						size="small"
						onClick={() => setDrawerOpen((open) => !open)}
						sx={{
							border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
							backgroundColor: alpha(theme.palette.background.paper, 0.6),
							backdropFilter: 'blur(8px)'
						}}
					>
						{!drawerOpen ? <ChevronRight /> : <ChevronLeft />}
					</IconButton>
				</Box>

				<SidebarMenu closed={!drawerOpen}></SidebarMenu>
			</Drawer>
			<Box
				id="main-content"
				component="main"
				tabIndex={-1}
				className="glass-surface"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					paddingTop: toolbarHeight + 'rem',
					paddingBottom: showAdvertisement ? adsHeight + 'rem' : 0,
					minHeight: '100vh',
					gap: 2,
					'&:focus': { outline: 'none' },
					// The skip link sends keyboard focus to this landmark; show a visible
					// indicator for keyboard users (WCAG 2.4.7) without flashing an outline
					// when the container is clicked with a mouse.
					'&:focus-visible': {
						outline: `3px solid ${theme.palette.primary.main}`,
						outlineOffset: '-3px'
					},
					...(isAnaPage && { height: '100vh', overflow: 'hidden' }),
					...(drawerOpen && {
						...openedMixinContainer(theme)
					}),
					...(!drawerOpen && {
						...closedMixinContainer(theme)
					})
				}}
			>
				{header}
				<Container
					maxWidth={isWideContentPage ? false : 'lg'}
					sx={{
						display: 'flex',
						justifyContent: 'center',
						py: 2,
						px: isWideContentPage ? { xs: 4.5, md: 13.5, lg: 22.5 } : undefined,
						flex: 1,
						...(isAnaPage && { minHeight: 0 })
					}}
				>
					<Suspense
						fallback={
							<Box
								sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}
							>
								<CircularProgress size={32} />
							</Box>
						}
					>
						{children}
					</Suspense>
					{aiChatbotEnabled && !isAnaPage && (
						<Portal>
							<DraggableChatbot
								handleOpenAiAssistant={handleOpenAiAssistant}
								setAIAgentsModalOpen={setAIAgentsModalOpen}
							/>
						</Portal>
					)}
				</Container>
				{actions}
			</Box>
			{showAdvertisement && (
				<Box
					className="glass-surface"
					sx={{
						position: 'fixed',
						bottom: 0,
						height: adsHeight + 'rem',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						...(drawerOpen && {
							...openedMixinAds(theme)
						}),
						...(!drawerOpen && {
							...closedMixinAds(theme)
						}),
						zIndex: 1000
					}}
				>
					<Ads />
				</Box>
			)}

			<AiAssistant open={aiAssistantOpen} onClose={handleCloseAiAssistant} />
			<MultiAgents
				open={aIAgentsModalOpen}
				onClose={() => setAIAgentsModalOpen(false)}
				agentClick={setSelectedAgent}
			/>
			<DocumentRequestsAssistant
				open={selectedAgent === 'document-requests'}
				onClose={() => setSelectedAgent(null)}
			/>
			<DailyUpdatesAssistant open={selectedAgent === 'daily-updates'} onClose={() => setSelectedAgent(null)} />
		</>
	);
};

const MainLayout = ({
	children,
	header,
	actions
}: {
	children?: React.ReactElement | React.ReactElement[];
	header?: React.ReactElement;
	actions?: React.ReactElement;
}) => {
	const { isMobile } = useWindowSize();
	const { tourActive, setTourActive } = useTutorial();
	const location = useLocation();
	const [steps, setSteps] = useState<Step[]>([]);
	const { authenticated, userId } = useAuthenticatedUser();
	const dispatch = useAppDispatch();
	const platformSettingsLoaded = useAppSelector((state) => state.platformSettings.loaded);
	const featureFlagsLoaded = useAppSelector((state) => state.featureFlags.loaded);
	const featureFlagsLoading = useAppSelector((state) => state.featureFlags.loading);
	const featureFlags = useAppSelector((state) => state.featureFlags?.flags);

	// Fetch platform settings and feature flags from backend on first mount.
	// featureFlagsLoading is intentionally omitted from deps to avoid a
	// retry loop when the fetch rejects (loading true→false would re-trigger).
	useEffect(() => {
		if (authenticated && !platformSettingsLoaded) {
			dispatch(fetchPlatformSettings());
		}
		if (authenticated && !featureFlagsLoaded && !featureFlagsLoading) {
			dispatch(fetchFeatureFlags());
			// When each flag was last switched on. A feature released dark takes its
			// "new" window from that moment rather than from its release date, so
			// without this nothing flag-gated is ever badged. Fetched beside the
			// flags because it is read wherever they are.
			dispatch(fetchFeatureFlagEnabledAt());
		}
	}, [authenticated, platformSettingsLoaded, featureFlagsLoaded, dispatch]);

	// Add event listener to track when user closes the browser/tab
	useEffect(() => {
		const handlePageUnload = (event: BeforeUnloadEvent) => {
			// Try to detect if it's a refresh
			const isReload =
				event?.type === 'beforeunload' &&
				(performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';
			if (!isReload && authenticated && userId) {
				const lastLogoutKey = `lastLogout_${userId}`;
				const currentTime = new Date().toISOString();
				localStorage.setItem(lastLogoutKey, currentTime);
			}
		};

		window.addEventListener('beforeunload', handlePageUnload);

		return () => {
			window.removeEventListener('beforeunload', handlePageUnload);
		};
	}, [authenticated, userId]);

	useEffect(() => {
		const appTourEnabled = isFeatureFlagEnabled(featureFlags, 'app-tour-enabled');
		// If an admin has disabled the App Tour, make sure a previously persisted
		// tourActive flag can't keep the walkthrough running.
		if (featureFlagsLoaded && !appTourEnabled) {
			if (tourActive) {
				setTourActive(false);
			}
			setSteps([]);
			return;
		}
		if (tourActive && location.pathname === '/wallet/' && isMobile) {
			setSteps(filterTourSteps(stepsMobilePage1, featureFlags));
		} else if (tourActive && location.pathname === '/wallet/' && !isMobile) {
			setSteps(filterTourSteps(stepsDesktopPage1, featureFlags));
		} else if (tourActive && location.pathname === '/verifications/') {
			setSteps(filterTourSteps(stepsDesktopAndMobilePage2, featureFlags));
		} else if (tourActive && location.pathname === '/account') {
			setSteps(filterTourSteps(stepsDesktopAndMobilePage3, featureFlags));
		} else if (tourActive && location.pathname === '/journey/my-profiles') {
			setSteps(filterTourSteps(stepsDesktopAndMobilePage4, featureFlags));
		}
	}, [featureFlags, featureFlagsLoaded, tourActive, location.pathname, isMobile, setTourActive]);

	// Sync glass intensity CSS custom properties from platform settings
	const glassEnabled = useAppSelector((state) => state.platformSettings.glassEnabled);
	const glassIntensity = useAppSelector((state) => state.platformSettings.glassIntensity);
	useEffect(() => {
		if (!glassEnabled) {
			document.documentElement.style.setProperty('--glass-blur', '0px');
			document.documentElement.classList.add('glass-disabled');
		} else {
			const blur = Math.round((glassIntensity / 100) * 30);
			document.documentElement.style.setProperty('--glass-blur', `${blur}px`);
			document.documentElement.classList.remove('glass-disabled');
		}
	}, [glassEnabled, glassIntensity]);

	return isMobile ? (
		<>
			<SkipLink targetId="main-content" />
			<MobileLayout {...{ header, actions, tourActive, setTourActive, steps }}>{children}</MobileLayout>
		</>
	) : (
		<>
			<SkipLink targetId="main-content" />
			<DesktopLayout {...{ header, actions, tourActive, setTourActive, steps }}>{children}</DesktopLayout>
		</>
	);
};

export default MainLayout;
